import { I as Icon, i as iconDefinition } from './definition28.js';
import { D as DOM, V as VividElement, a as attr, o as observable, r as replaces, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { i as keyArrowLeft, h as keyArrowRight, a as keySpace, k as keyEnter } from './key-codes.js';
import { b as AffixIcon, a as affixIconTemplateFactory } from './affix.js';
import { H as HostSemantics, a as applyHostSemantics } from './host-semantics.js';
import { s as slotted, e as elements } from './slotted.js';
import { c as classNames } from './class-names.js';
import { w as when } from './when.js';

const styles = ":host(:focus-visible){outline:none}:host([disabled]){pointer-events:none}.base{--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: transparent;--_appearance-color-outline: transparent}.base:where(.hover,:hover):where(:not(.disabled,:disabled,.readonly)){--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: var(--_connotation-color-faint);--_appearance-color-outline: transparent}.base:where(.disabled,:disabled){--_appearance-color-text: var(--vvd-color-neutral-300);--_appearance-color-fill: transparent;--_appearance-color-outline: transparent}.base:where(.active,:active):where(:not(.disabled,:disabled)){--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: var(--_connotation-color-soft);--_appearance-color-outline: transparent}.base:where(.selected):where(:not(.disabled,:disabled)){--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: var(--_connotation-color-soft);--_appearance-color-outline: transparent}.base:where(.selected):where(.hover,:hover):where(:not(.disabled,:disabled,.readonly)){--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: var(--_connotation-color-dim);--_appearance-color-outline: transparent}.base.connotation-cta{--_connotation-color-primary: var(--vvd-menu-item-cta-primary, var(--vvd-color-cta-500));--_connotation-color-primary-text: var(--vvd-menu-item-cta-primary-text, var(--vvd-color-canvas));--_connotation-color-primary-increment: var(--vvd-menu-item-cta-primary-increment, var(--vvd-color-cta-600));--_connotation-color-firm: var(--vvd-menu-item-cta-firm, var(--vvd-color-cta-600));--_connotation-color-faint: var(--vvd-menu-item-cta-faint, var(--vvd-color-cta-50));--_connotation-color-soft: var(--vvd-menu-item-cta-soft, var(--vvd-color-cta-100));--_connotation-color-pale: var(--vvd-menu-item-cta-pale, var(--vvd-color-cta-300));--_connotation-color-dim: var(--vvd-menu-item-cta-dim, var(--vvd-color-cta-200))}.base:not(.connotation-cta){--_connotation-color-primary: var(--vvd-menu-item-accent-primary, var(--vvd-color-canvas-text));--_connotation-color-primary-text: var(--vvd-menu-item-accent-primary-text, var(--vvd-color-canvas));--_connotation-color-primary-increment: var(--vvd-menu-item-accent-primary-increment, var(--vvd-color-neutral-800));--_connotation-color-firm: var(--vvd-menu-item-accent-firm, var(--vvd-color-canvas-text));--_connotation-color-faint: var(--vvd-menu-item-accent-faint, var(--vvd-color-neutral-50));--_connotation-color-soft: var(--vvd-menu-item-accent-soft, var(--vvd-color-neutral-100));--_connotation-color-pale: var(--vvd-menu-item-accent-pale, var(--vvd-color-neutral-300));--_connotation-color-dim: var(--vvd-menu-item-accent-dim, var(--vvd-color-neutral-200))}.base{--_menu-item-padding-inline: calc(1px*(40 + 4*clamp(-1, var(--vvd-size-density, 0), 2))*.4) }.base{display:flex;box-sizing:border-box;align-items:center;border-radius:8px;background-color:var(--_appearance-color-fill);box-shadow:inset 0 0 0 1px var(--_appearance-color-outline);inline-size:100%}.base:not(.two-lines){gap:12px;min-block-size:calc(1px*(40 + 4*clamp(-1,var(--vvd-size-density, 0),2)));padding-block:8px;padding-inline:var(--_menu-item-padding-inline)}.base.two-lines{gap:16px;min-block-size:calc(1px*(40 + 4*clamp(-1,var(--vvd-size-density, 0),2)) + 1px*(24 + 4*clamp(-1,var(--vvd-size-density, 0),2)));padding-block:12px;padding-inline:var(--_menu-item-padding-inline)}@supports (user-select: none){.base{user-select:none}}.base:not(.disabled){cursor:pointer}:host(:not([role=presentation]):focus-visible) .base{box-shadow:0 0 0 4px color-mix(in srgb,var(--vvd-color-cta-500),transparent 85%),inset 0 0 0 3px var(--focus-stroke-gap-color, currentColor);outline:1px solid var(--focus-stroke-color, var(--vvd-color-cta-500));outline-offset:calc(-1px - var(--focus-inset, 0px));--focus-stroke-gap-color: transparent}.icon{flex-shrink:0;font-size:calc(1px*(40 + 4*clamp(-1,var(--vvd-size-density, 0),2))/2);line-height:1}.action,.decorative{display:flex;place-content:center}.action{color:var(--_appearance-color-text)}.base.trailing .action,.base.has-meta .action{order:2}:host(:not([check-appearance],[checked],[disabled])) .action{color:var(--vvd-color-neutral-500)}.base:not(.disabled) .decorative{color:var(--vvd-color-neutral-600)}.base.disabled .decorative{color:var(--vvd-color-neutral-200)}.base.has-meta .decorative{order:1}.chevron{order:3}.text{display:flex;overflow:hidden;flex-direction:column;gap:4px;margin-inline-end:auto}.text-primary,.text-secondary{display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical;font:var(--vvd-typography-base)}.text-primary{font:var(--vvd-typography-base);-webkit-line-clamp:var(--text-primary-line-clamp, 1)}.base:not(.disabled) .text-primary{color:var(--vvd-color-canvas-text)}.base.disabled .text-primary{color:var(--_appearance-color-text)}.base.two-lines .text-primary{font:var(--vvd-typography-base-bold)}.text-secondary{color:var(--vvd-color-neutral-800);font:var(--vvd-typography-base-condensed);-webkit-line-clamp:var(--text-secondary-line-clamp, 1)}.base.two-lines .text-secondary{color:var(--vvd-color-neutral-600)}.base.disabled .text-secondary{color:var(--_appearance-color-text)}.base.selected:not(.disabled) .text-secondary{color:var(--vvd-color-neutral-800)}:host([internal-part]) .text-primary{font:var(--_text-primary-custom-size, var--vvd-typography-base)}";

const MenuItemRole = {
  menuitem: "menuitem",
  menuitemcheckbox: "menuitemcheckbox",
  menuitemradio: "menuitemradio",
  presentation: "presentation"
};

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
class MenuItem extends HostSemantics(AffixIcon(VividElement)) {
  constructor() {
    super();
    // eslint-disable-next-line @repo/repo/no-attribute-default-value
    this.role = MenuItemRole.menuitem;
    this.checked = false;
    /**
     * @internal
     */
    this.handleMenuItemClick = (e) => {
      if (this._isSyntheticClickEvent(e)) {
        return true;
      }
      this.invoke();
      return Boolean(this._isPresentational);
    };
    /**
     * @internal
     */
    this.handleMouseOver = (_) => {
      if (this.disabled || !this.submenu || this.expanded) {
        return false;
      }
      this.expanded = true;
      return false;
    };
    /**
     * @internal
     */
    this.handleMouseOut = (_) => {
      if (!this.expanded || this.contains(document.activeElement)) {
        return false;
      }
      this.expanded = false;
      return false;
    };
    this.invoke = () => {
      if (this.disabled || this._isPresentational) {
        return;
      }
      switch (this.controlType) {
        case "checkbox":
          this.checked = !this.checked;
          break;
        case "radio":
          if (!this.checked) {
            this.checked = true;
          }
          break;
        default:
          if (this.submenu) {
            this.expanded = true;
          } else {
            this.$emit("change");
          }
          break;
      }
    };
    this.checkTrailing = false;
    /**
     * @internal
     */
    this.handleMenuItemKeyDown = (e) => {
      if (e.defaultPrevented) {
        return false;
      }
      switch (e.key) {
        case keyEnter:
        case keySpace:
          this.invoke();
          if (!this.disabled) {
            this.#emitSyntheticClick();
          }
          return false;
        case keyArrowRight:
          if (this.submenu) {
            this.expanded = true;
            this.#emitSyntheticClick();
          }
          return false;
        case keyArrowLeft:
          if (this.expanded) {
            this.#emitSyntheticClick();
            this.expanded = false;
            this.focus();
            return false;
          }
      }
      return true;
    };
    this.#syntheticClickEvents = /* @__PURE__ */ new WeakSet();
    this.addEventListener("expanded-change", this.#expandedChange);
  }
  /**
   * @internal
   */
  expandedChanged() {
    if (this.$fastController.isConnected) {
      if (this.submenu && !this.expanded) {
        this.submenu.collapseExpandedItem();
      }
      this.$emit("expanded-change", this, { bubbles: false });
    }
  }
  #ensureRoleMatchesPresentational() {
    if (this._isPresentational) {
      if (this.role === "menuitem") {
        this.role = "presentation";
      }
    } else if (this.role === "presentation") {
      this.role = MenuItemRole.menuitem;
    }
  }
  /**
   * @internal
   */
  _isPresentationalChanged() {
    if (this.$fastController.isConnected) {
      this.#ensureRoleMatchesPresentational();
    }
  }
  /**
   * @internal
   */
  checkedChanged() {
    if (this.$fastController.isConnected) {
      this.$emit("change");
    }
  }
  /**
   * @internal
   */
  connectedCallback() {
    super.connectedCallback();
    DOM.queueUpdate(() => {
      this._isPresentational = this._isPresentational ?? true;
      this.#ensureRoleMatchesPresentational();
    });
  }
  /**
   * @internal
   */
  slottedSubmenuChanged() {
    if (this.submenu) {
      this.submenu.anchor = this;
      this.submenu.placement = "right-start";
      this.submenu.collapseExpandedItem = () => this.#collapseExpandedItem();
      this.submenu._popupOffset = 5;
    }
  }
  /**
   * @internal
   */
  get submenu() {
    if (this.slottedSubmenu?.length) {
      return this.slottedSubmenu[0];
    } else {
      return null;
    }
  }
  /**
   * @internal
   */
  get hasSubmenu() {
    return this.submenu !== null;
  }
  #collapseExpandedItem() {
    this.expanded = false;
  }
  #expandedChange() {
    if (this.submenu) {
      this.submenu.open = this.expanded;
    }
  }
  #syntheticClickEvents;
  /**
   * @internal
   */
  _isSyntheticClickEvent(event) {
    return this.#syntheticClickEvents.has(event);
  }
  #emitSyntheticClick() {
    const mouseEvent = new MouseEvent("click", {
      bubbles: true,
      composed: true
    });
    this.#syntheticClickEvents.add(mouseEvent);
    this.dispatchEvent(mouseEvent);
  }
}
__decorateClass([
  attr({ mode: "boolean" })
], MenuItem.prototype, "disabled");
__decorateClass([
  attr({ mode: "boolean" })
], MenuItem.prototype, "expanded");
__decorateClass([
  attr
], MenuItem.prototype, "role");
__decorateClass([
  observable
], MenuItem.prototype, "_isPresentational");
__decorateClass([
  replaces({
    deprecatedPropertyName: "role",
    fromDeprecated: (role) => {
      switch (role) {
        case "menuitemcheckbox":
          return "checkbox";
        case "menuitemradio":
          return "radio";
        default:
          return void 0;
      }
    },
    toDeprecated: (controlType) => {
      switch (controlType) {
        case "checkbox":
          return "menuitemcheckbox";
        case "radio":
          return "menuitemradio";
        default:
          return "menuitem";
      }
    }
  }),
  attr({ attribute: "control-type" })
], MenuItem.prototype, "controlType");
__decorateClass([
  attr({ mode: "boolean" })
], MenuItem.prototype, "checked");
__decorateClass([
  attr
], MenuItem.prototype, "text");
__decorateClass([
  attr({ attribute: "text-secondary" })
], MenuItem.prototype, "textSecondary");
__decorateClass([
  attr
], MenuItem.prototype, "connotation");
__decorateClass([
  attr({ mode: "boolean", attribute: "check-trailing" })
], MenuItem.prototype, "checkTrailing");
__decorateClass([
  attr({ attribute: "check-appearance" })
], MenuItem.prototype, "checkedAppearance");
__decorateClass([
  observable
], MenuItem.prototype, "metaSlottedContent");
__decorateClass([
  observable
], MenuItem.prototype, "trailingMetaSlottedContent");
__decorateClass([
  observable
], MenuItem.prototype, "slottedSubmenu");

const menuName = "menu";

const getIndicatorIcon = (x) => {
  if (x.checkedAppearance === "tick-only") {
    return x.checked ? "check-line" : "";
  }
  const iconType = x.controlType === "checkbox" ? "checkbox" : "radio";
  const iconStatus = x.checked ? "checked" : "unchecked";
  return `${iconType}-${iconStatus}-2-line`;
};
const getClasses = ({
  connotation,
  disabled,
  checked,
  controlType,
  text: text2,
  textSecondary,
  icon,
  metaSlottedContent,
  checkTrailing
}) => classNames(
  "base",
  [`connotation-${connotation}`, Boolean(connotation)],
  ["disabled", Boolean(disabled)],
  ["selected", Boolean(controlType && checked)],
  ["trailing", Boolean(controlType && (checkTrailing || Boolean(icon)))],
  ["item-checkbox", controlType === "checkbox"],
  ["item-radio", controlType === "radio"],
  ["two-lines", Boolean(text2?.length) && Boolean(textSecondary?.length)],
  ["has-meta", Boolean(metaSlottedContent?.length)]
);
function checkIndicator(context) {
  const iconTag = context.tagFor(Icon);
  return html`${when(
    (x) => Boolean(x.controlType),
    html`<span class="action"><${iconTag} class="icon" name="${(x) => getIndicatorIcon(x)}"></${iconTag}></span>`
  )}`;
}
function text() {
  return html`${when(
    (x) => x.text || x.textSecondary,
    html`<span class="text">
			${when(
      (x) => x.text,
      html`<span class="text-primary">${(x) => x.text}</span>`
    )}
			${when(
      (x) => x.textSecondary,
      html`<span class="text-secondary">${(x) => x.textSecondary}</span>`
    )}
		</span>`
  )}`;
}
const MenuItemTemplate = (context) => {
  const affixIconTemplate = affixIconTemplateFactory(context);
  const iconTag = context.tagFor(Icon);
  return html`
		<template
			${applyHostSemantics({
    role: MenuItemRole.menuitem,
    ariaHasPopup: (x) => x.hasSubmenu ? "menu" : void 0,
    ariaChecked: (x) => x.controlType ? x.checked : void 0,
    ariaDisabled: (x) => x.disabled,
    ariaExpanded: (x) => x.expanded
  })}
			@keydown="${(x, c) => x.handleMenuItemKeyDown(c.event)}"
			@click="${(x, c) => x.handleMenuItemClick(c.event)}"
			@mouseover="${(x, c) => x.handleMouseOver(c.event)}"
			@mouseout="${(x, c) => x.handleMouseOut(c.event)}"
		>
			<div class="${getClasses}">
				<slot name="meta" ${slotted("metaSlottedContent")}></slot>
				${checkIndicator(context)}
				${when(
    (x) => x.icon,
    html`<span class="decorative"
						>${(x) => affixIconTemplate(x.icon)}</span
					>`
  )}
				${text()}
				<slot
					name="trailing-meta"
					${slotted("trailingMetaSlottedContent")}
				></slot>
				${when(
    (x) => x.hasSubmenu,
    html`<${iconTag} class="chevron" name="chevron-right-line"></${iconTag}>`
  )}
			</div>
			<slot
				name="submenu"
				${slotted({
    property: "slottedSubmenu",
    filter: elements(context.tagForNonDependency(menuName))
  })}
			></slot>
		</template>
	`;
};

const menuItemDefinition = defineVividComponent(
  "menu-item",
  MenuItem,
  MenuItemTemplate,
  [iconDefinition],
  {
    styles
  }
);
const registerMenuItem = createRegisterFunction(menuItemDefinition);

export { MenuItem as M, MenuItemRole as a, menuName as b, menuItemDefinition as m, registerMenuItem as r };
